import { Injectable, inject } from '@angular/core';
import { ToastController } from '@ionic/angular/standalone';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { Subject } from 'rxjs';

import { MsalService } from '@azure/msal-angular';
import type { AccountInfo, SilentRequest } from '@azure/msal-browser';

import { SignalRHubEventNames } from '@postal-bridge/shared/models';
import { environment } from 'environments/environment';
import { CustomerSessionService } from 'app/core/session/customer-session.service';

@Injectable({ providedIn: 'root' })
export class CustomerRealTimeNotificationsService {
  private readonly msal = inject(MsalService);
  private readonly session = inject(CustomerSessionService);
  private readonly toastCtrl = inject(ToastController);

  private hub: HubConnection | null = null;
  private starting: Promise<void> | null = null;
  private joinedCustomerId: number | null = null;

  private readonly inboxRefreshSubject = new Subject<void>();
  readonly inboxRefresh$ = this.inboxRefreshSubject.asObservable();

  async ensureStarted(): Promise<void> {
    if (this.hub?.state === HubConnectionState.Connected) return;
    if (this.starting) return this.starting;

    const hubUrl = this.resolveHubUrl();
    if (!hubUrl) return;

    this.hub = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: async () => {
          try {
            const token = await this.getAccessToken();
            return token ?? '';
          } catch {
            return '';
          }
        },
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    this.hub.onclose(() => {
      // ignore
    });

    this.hub.onreconnected(async () => {
      if (this.joinedCustomerId) {
        await this.safeJoinCustomerGroup(this.joinedCustomerId);
      }
    });

    this.registerHandlers(this.hub);

    this.starting = this.hub
      .start()
      .then(async () => {
        // Attempt to join customer group as soon as possible.
        try {
          const me = await this.session.ensureMe();
          this.joinedCustomerId = me.id;
          await this.safeJoinCustomerGroup(me.id);
        } catch {
          // If /me fails here, we'll try again when someone calls ensureJoined().
        }
      })
      .catch(() => {
        // ignore startup errors (will retry on next ensureStarted)
      })
      .finally(() => {
        this.starting = null;
      });

    return this.starting;
  }

  async ensureJoined(): Promise<void> {
    await this.ensureStarted();
    if (!this.hub || this.hub.state !== HubConnectionState.Connected) return;

    try {
      const me = await this.session.ensureMe();
      if (this.joinedCustomerId === me.id) return;
      this.joinedCustomerId = me.id;
      await this.safeJoinCustomerGroup(me.id);
    } catch {
      // ignore
    }
  }

  private registerHandlers(hub: HubConnection): void {
    // In practice we care most about MailQueueUpdated for customers.
    hub.on(SignalRHubEventNames.MailQueueUpdated as any, async () => {
      this.inboxRefreshSubject.next();
      await this.presentToast('New mail received.');
    });

    // Fallback for older clients/servers that use raw string.
    hub.on('MailQueueUpdated', async () => {
      this.inboxRefreshSubject.next();
      await this.presentToast('New mail received.');
    });
  }

  private async safeJoinCustomerGroup(customerId: number): Promise<void> {
    if (!this.hub) return;
    if (this.hub.state !== HubConnectionState.Connected) return;

    try {
      await this.hub.invoke('JoinCustomerGroup', customerId);
    } catch {
      // ignore
    }
  }

  private async getAccessToken(): Promise<string | null> {
    const instance = this.msal.instance;
    const account: AccountInfo | undefined = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    if (!account) return null;

    const request: SilentRequest = {
      account,
      scopes: (environment as any)?.apiConfig?.scopes ?? [],
    };

    const result = await instance.acquireTokenSilent(request);
    return result?.accessToken ?? null;
  }

  private resolveHubUrl(): string | null {
    const raw = String((environment as any).signalRHubUrl ?? '').trim();
    if (!raw) return null;

    // If already absolute, use as-is.
    if (/^https?:\/\//i.test(raw)) return raw;

    // Browser: resolve relative to current origin.
    if (typeof window !== 'undefined' && window.location?.origin) {
      const base = window.location.origin.replace(/\/+$/, '');
      const path = raw.startsWith('/') ? raw : `/${raw}`;
      return `${base}${path}`;
    }

    return raw;
  }

  private async presentToast(message: string): Promise<void> {
    try {
      const toast = await this.toastCtrl.create({
        message,
        duration: 2500,
        position: 'bottom',
      });
      await toast.present();
    } catch {
      // ignore
    }
  }
}
